import dagre, { Node as DagreNode } from "@dagrejs/dagre";
import {
    Node as ReactFlowNode, Position, Node, Edge,
    XYPosition,
} from "@xyflow/react";
import { ACTION, GROUP, NODE_TYPE } from "./constants";
import { WorkflowStep } from "../../../__generated__/api";

const dagreGraph = new dagre.graphlib.Graph().setDefaultEdgeLabel(() => ({}));

export type NodeWithMetadata = DagreNode & ReactFlowNode;

type LayoutPosition = {
    x: number;
    y: number;
};

const SUB_ACTION_DIMENSTIONS = {
    width: 100,
    height: 100,
};

const VERTICAL_GAP = 16;

function calculateActionNodePosition(
    actionNode: Node,
    parent: Node,
    maxY: number,
): XYPosition | null {
    const parentXOffset = parent.position.x;

    if (parent.type === GROUP.ACTIONS
        || parent.type === GROUP.ACTIONS_CREATING
        || parent.type === GROUP.WEBHOOK_ACTIONS
        || parent.type === GROUP.SUBACTION
        || parent.type === GROUP.CONDITION
    ) {
        return {
            x: parentXOffset + 40,
            y: maxY + VERTICAL_GAP,
        };
    }

    const siblings = (parent.data.children ?? []) as WorkflowStep[];
    let elementIdx = siblings.findIndex(sibling => sibling.id === actionNode.id);

    // new action node, without siblings
    if (elementIdx === -1) {
        elementIdx = siblings.length;
    }

    const elementHeight = actionNode.measured?.height ?? 0;
    const parentNodeHeight = parent.measured?.height ?? 0;
    const parentYOffset = parent.position.y;

    const elementPositionRelativeToParent = elementIdx * elementHeight + elementIdx * VERTICAL_GAP + VERTICAL_GAP;

    const currentElementYOffset = parentYOffset + parentNodeHeight + elementPositionRelativeToParent;

    return {
        x: parentXOffset + 40,
        y: Math.max(currentElementYOffset, maxY + VERTICAL_GAP),
    };
}

export const isGroupNode = (node: Node) => node.data.__typename === "WorkflowStepGroup"
|| node.data.__typename === "WorkflowServiceChildAction";

function getPositionFromRank(
    maxY: number,
    nodeWithPosition: NodeWithMetadata,
    parentWithPosition?: NodeWithMetadata,
): LayoutPosition {
    if (nodeWithPosition.type === NODE_TYPE.EMPTY_FLOW) {
        return { x: nodeWithPosition.x / 2, y: -nodeWithPosition.y / 2 };
    }

    // Handle request created node
    if (!parentWithPosition || !parentWithPosition.type) {
        return { x: nodeWithPosition.x, y: nodeWithPosition.y - 2 };
    }

    const parentPosition = parentWithPosition.data.measured as LayoutPosition;
    const parentX = parentPosition.x;
    const parentY = parentPosition.y;

    if (nodeWithPosition.type === ACTION.NEW) {
        const position = calculateActionNodePosition(nodeWithPosition, parentWithPosition, maxY);
        return { x: position?.x ?? 0, y: position?.y ?? 0 };
    }

    const isParentGroupNode = isGroupNode(parentWithPosition);
    const isParentActionNode = !isParentGroupNode;

    if (nodeWithPosition.type === GROUP.SUBACTION || nodeWithPosition.type === GROUP.CONDITION) {
        return {
            x: parentX + (parentWithPosition.measured?.width ?? 0) + 25,
            y: parentY + (parentWithPosition.measured?.height ?? 0) / 2 - (nodeWithPosition.measured?.height ?? 0) / 2,
        };
    }

    // Handle default case for group nodes
    if (isParentActionNode) {
        return {
            x: parentX + (parentWithPosition.measured?.width ?? 0) + 25,
            y: parentY + (parentWithPosition.measured?.height ?? 0) / 2 - (nodeWithPosition.measured?.height ?? 0) / 2,
        };
    }

    // Handle default case for action nodes
    if (isParentGroupNode) {
        const actionNodePosition = calculateActionNodePosition(nodeWithPosition, parentWithPosition, maxY);
        return {
            x: actionNodePosition?.x ?? 0,
            y: actionNodePosition?.y ?? 0,
        };
    }

    return { x: nodeWithPosition.x, y: nodeWithPosition.y };
}

export function getLayoutedElements(nodes: Node[], edges: Edge[]) {
    dagreGraph.setGraph({
        rankdir: "LR",
        align: "UL",
        ranksep: 25,
        nodesep: 16,
    });

    nodes.forEach(node => {
        dagreGraph.setNode(node.id, {
            ...node,
            width: node.measured?.width ?? SUB_ACTION_DIMENSTIONS.width,
            height: node.measured?.height ?? SUB_ACTION_DIMENSTIONS.height,
        });
    });

    edges.forEach(edge => {
        dagreGraph.setEdge(edge.source, edge.target);
    });

    dagre.layout(dagreGraph);

    const processedNodes: Node[] = [];
    let maxY = 0;

    nodes.forEach(node => {
        const nodeWithPosition = dagreGraph.node(node.id);
        const parent = node.data.parentId;
        const parentPosition = processedNodes.find(nd => nd.id === parent);

        const newPosition = getPositionFromRank(
            maxY,
            nodeWithPosition as NodeWithMetadata,
            parentPosition as NodeWithMetadata,
        );

        maxY = Math.max(maxY, newPosition.y + (nodeWithPosition.height ?? 0));

        const newNode: Node = {
            ...node,
            targetPosition: Position.Left,
            sourcePosition: Position.Right,
            position: newPosition,
            data: {
                ...node.data,
                measured: newPosition,
            },
        };

        processedNodes.push(newNode);
    });

    return { nodes: processedNodes, edges };
}

export function calculateNewNodePosition(parentId: string, nodes: Node[]) {
    const parentNode = nodes.find(node => node.id === parentId);
    if (!parentNode) {
        return { x: 0, y: 0 };
    }

    // Find all siblings of the new node (nodes with the same parent)
    const siblings = nodes.filter(node => node.data?.parentId === parentId);

    // If there are siblings, place the new node below the last sibling
    if (siblings.length > 0) {
        const sortedSiblings = [...siblings].sort((nodeA, nodeB) => nodeA.position.y - nodeB.position.y);
        const lastSibling = sortedSiblings[sortedSiblings.length - 1];
        return {
            x: lastSibling.position.x,
            y: lastSibling.position.y + 150, // Add vertical spacing
        };
    }

    // If no siblings, position relative to the parent
    return {
        x: parentNode.position.x + 300, // Add horizontal spacing
        y: parentNode.position.y,
    };
}

export default dagreGraph;
